/**
 * Theme Tokens
 *
 * Semantic design tokens that bridge API theme data and the design system.
 * These tokens provide a consistent interface for theme-aware styling.
 */

// ============================================================================
// COLOR TOKENS
// ============================================================================

/**
 * Default color palette (fallback when no theme is provided)
 */
export const DEFAULT_COLORS = {
  primary: '#2A60FF', // Bright blue - Template Overview card
  secondary: '#2563EB', // Modern blue - Avg Execution Time card
  accent: '#28a745',
  background: '#ffffff',
  surface: '#f0f8ff',
  text: '#000000',
};

/**
 * Color roles mapping
 * Maps API theme color fields to semantic color roles
 */
export const COLOR_ROLES = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  ACCENT: 'accent',
  BACKGROUND: 'background',
  SURFACE: 'surface',
  TEXT: 'text',
  TEXT_SECONDARY: 'textSecondary',
  BORDER: 'border',
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  INFO: 'info',
};

/**
 * Surface elevation levels
 * Used for layering and depth perception
 */
export const SURFACE_LEVELS = {
  BASE: 0, // background_color
  RAISED: 1, // surface_color
  OVERLAY: 2, // dialogs, modals
  POPUP: 3, // tooltips, popovers
};

// ============================================================================
// TYPOGRAPHY TOKENS
// ============================================================================

/**
 * Font family presets
 */
export const FONT_PRESETS = [
  {
    name: 'Modern Professional',
    value: "'Inter', 'Roboto', 'Helvetica Neue', 'Arial', sans-serif",
  },
  {
    name: 'Corporate Clean',
    value: "'Helvetica Neue', 'Arial', 'Roboto', sans-serif",
  },
  {
    name: 'Contemporary',
    value: "'Poppins', 'Montserrat', 'Arial', sans-serif",
  },
  {
    name: 'System Native',
    value:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', sans-serif",
  },
];

/**
 * Individual font options organized by category
 */
export const FONT_OPTIONS = {
  SANS_SERIF: [
    { name: 'Inter', value: 'Inter' },
    { name: 'Roboto', value: 'Roboto' },
    { name: 'Open Sans', value: 'Open Sans' },
    { name: 'Lato', value: 'Lato' },
    { name: 'Montserrat', value: 'Montserrat' },
    { name: 'Poppins', value: 'Poppins' },
    { name: 'Source Sans Pro', value: 'Source Sans Pro' },
    { name: 'Nunito', value: 'Nunito' },
    { name: 'Raleway', value: 'Raleway' },
    { name: 'Ubuntu', value: 'Ubuntu' },
  ],
  SERIF: [
    { name: 'Playfair Display', value: 'Playfair Display' },
    { name: 'Merriweather', value: 'Merriweather' },
    { name: 'Lora', value: 'Lora' },
    { name: 'Crimson Text', value: 'Crimson Text' },
    { name: 'Libre Baskerville', value: 'Libre Baskerville' },
  ],
  MONOSPACE: [
    { name: 'Fira Code', value: 'Fira Code' },
    { name: 'Source Code Pro', value: 'Source Code Pro' },
    { name: 'JetBrains Mono', value: 'JetBrains Mono' },
    { name: 'Consolas', value: 'Consolas' },
  ],
  SYSTEM: [
    { name: 'Helvetica Neue', value: 'Helvetica Neue' },
    { name: 'Arial', value: 'Arial' },
    { name: 'Georgia', value: 'Georgia' },
    { name: 'Times New Roman', value: 'Times New Roman' },
  ],
};

/**
 * Typography scale configuration
 */
export const TYPOGRAPHY_SCALE = {
  BASE_SIZE: 14, // Default base font size in px
  HEADING_MULTIPLIER: 1.5, // Multiplier for heading sizes
  LINE_HEIGHT: {
    TIGHT: 1.2,
    NORMAL: 1.5,
    RELAXED: 1.8,
  },
};

/**
 * Font weight scale
 */
export const FONT_WEIGHTS = {
  LIGHT: 300,
  REGULAR: 400,
  MEDIUM: 500,
  SEMI_BOLD: 600,
  BOLD: 700,
  EXTRA_BOLD: 800,
  BLACK: 900,
};

// ============================================================================
// SPACING TOKENS
// ============================================================================

/**
 * Spacing multipliers based on spacing_unit from API
 * Default spacing_unit = 8px
 */
export const SPACING_MULTIPLIERS = {
  XXS: 0.25, // 2px (8 * 0.25)
  XS: 0.5, // 4px (8 * 0.5)
  SM: 1, // 8px (8 * 1)
  MD: 1.5, // 12px (8 * 1.5)
  LG: 2, // 16px (8 * 2)
  XL: 2.5, // 20px (8 * 2.5)
  XXL: 3, // 24px (8 * 3)
  XXXL: 4, // 32px (8 * 4)
};

/**
 * Border radius multipliers based on border_radius from API
 * Default border_radius = 8px
 */
export const BORDER_RADIUS_SCALE = {
  NONE: 0,
  SM: 0.5, // border_radius * 0.5
  MD: 1, // border_radius * 1
  LG: 1.5, // border_radius * 1.5
  XL: 2, // border_radius * 2
  FULL: 9999, // Fully rounded
};

// ============================================================================
// COMPONENT TOKENS
// ============================================================================

/**
 * Default theme form data for creating new themes
 */
export const DEFAULT_THEME_FORM_DATA = {
  theme_name: '',
  theme_description: '',
  is_active: false,

  // Colors
  primary_color: DEFAULT_COLORS.primary,
  secondary_color: DEFAULT_COLORS.secondary,
  accent_color: DEFAULT_COLORS.accent,
  background_color: DEFAULT_COLORS.background,
  surface_color: DEFAULT_COLORS.surface,
  text_color: DEFAULT_COLORS.text,

  // Typography
  font_family: "'Inter', 'Roboto', 'Helvetica Neue', 'Arial', sans-serif",
  font_size: '14px',
  heading_font_size: '1.5rem',
  font_weight_normal: '400',
  font_weight_bold: '700',

  // Layout
  border_radius: 8,
  spacing_unit: 8,

  // Assets
  logo_url: '',
  favicon_url: '',

  // Custom
  custom_css: '',
};

/**
 * Theme status values
 */
export const THEME_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  DRAFT: 'draft',
};

/**
 * Table configuration constants
 */
export const TABLE_CONFIG = {
  ROWS_PER_PAGE_OPTIONS: [5, 10, 25, 50],
  DEFAULT_ROWS_PER_PAGE: 10,
};

// ============================================================================
// THEME PRESETS
// ============================================================================

/**
 * Pre-built theme configurations
 */
export const THEME_PRESETS = {
  LIGHT: {
    name: 'Light Theme',
    primary_color: '#00bfff',
    secondary_color: '#6d7e7c',
    accent_color: '#28a745',
    background_color: '#ffffff',
    surface_color: '#f0f8ff',
    text_color: '#000000',
  },
  DARK: {
    name: 'Dark Theme',
    primary_color: '#64b5f6',
    secondary_color: '#81c784',
    accent_color: '#ffb74d',
    background_color: '#121212',
    surface_color: '#1e1e1e',
    text_color: '#ffffff',
  },
  CORPORATE: {
    name: 'Corporate Theme',
    primary_color: '#1976d2',
    secondary_color: '#dc004e',
    accent_color: '#f57c00',
    background_color: '#fafafa',
    surface_color: '#ffffff',
    text_color: '#212121',
  },
};

// ============================================================================
// EXPORTS
// ============================================================================

const tokens = {
  DEFAULT_COLORS,
  COLOR_ROLES,
  SURFACE_LEVELS,
  FONT_PRESETS,
  FONT_OPTIONS,
  TYPOGRAPHY_SCALE,
  FONT_WEIGHTS,
  SPACING_MULTIPLIERS,
  BORDER_RADIUS_SCALE,
  DEFAULT_THEME_FORM_DATA,
  THEME_STATUS,
  TABLE_CONFIG,
  THEME_PRESETS,
};

export default tokens;
